import {
  SEL_DEFAULT_AREA_PLW,
  SEL_REPLACEMENT_STRATEGIES_PLW
} from '@/contentprofile/no_sel_composable'
import { goalbookUrls } from '@/util/links.js'
import { getCSRFHeaders, getHandleRequest } from '@/authentication/request_handler.ts'

const areasOfNeed = [
  {
    label: 'Pre-Academic Skills',
    value: 'Pre-Academic Skills',
    beta: false,
    dateAdded: new Date(2020, 2, 12) // March 12, 2020 (not Feb 12, 2020)
  },
  {
    label: 'Reading',
    value: 'Reading',
    iodSubject: true
  },
  {
    label: 'Writing',
    value: 'Writing',
    iodSubject: true
  },
  {
    label: 'Math',
    value: 'Math',
    iodSubject: true
  },
  {
    label: 'Alt Academics: Reading',
    value: 'Alt Academics: Reading',
    beta: false,
    dateAdded: new Date(2020, 2, 12)
  },
  {
    label: 'Alt Academics: Writing',
    value: 'Alt Academics: Writing',
    beta: false,
    dateAdded: new Date(2020, 10, 24)
  },
  {
    label: 'Alt Academics: Math',
    value: 'Alt Academics: Math',
    beta: false,
    dateAdded: new Date(2020, 2, 12)
  },
  {
    label: 'Executive Functioning',
    value: 'Executive Functioning',
  },
  {
    label: 'Behavior',
    value: 'Behavior',
    iodSubject: true
  },
  {
    label: 'Social and Emotional Learning',
    value: 'Social & Emotional Learning'
  },
  {
    label: 'Communication',
    value: 'Communication'
  },
  {
    label: 'Fine Motor Skills',
    value: 'Fine Motor Skills'
  },
  {
    label: 'Gross Motor Skills',
    value: 'Gross Motor Skills',
    dateAdded: new Date(2020, 7, 21) // August 21, 2020
  },
  {
    label: 'Functional and Life Skills',
    value: 'Functional'
  },
  {
    label: 'Transition',
    value: 'Transition',
    beta: false,
    dateAdded: new Date(2020, 2, 12)
  }
]

/** @param {{ selBlocked?: boolean, iodSubject?: boolean, blockedSubjects?: string[] }} [opts] */
export function getAreasOfNeed(opts = { selBlocked: false, iodSubject: false, blockedSubjects: [] }) {
  let areasToReturn = areasOfNeed.slice()
  if (opts && opts.selBlocked) {
    areasToReturn.find((area) => area.value === SEL_DEFAULT_AREA_PLW).label =
      SEL_REPLACEMENT_STRATEGIES_PLW
  }
  if (opts && opts.iodSubject) {
    areasToReturn = areasToReturn.filter((area) => area.iodSubject)
  }
  if (opts && opts.blockedSubjects && opts.blockedSubjects.length > 0) {
    areasToReturn = areasToReturn.filter((area) => !opts.blockedSubjects.includes(area.value))
  }
  return areasToReturn
}

const skillTypes = Object.freeze({
  IMPACT: Symbol('Impact'),
  SKILL: Symbol('Skill')
})

export const UNLOCKED_IMPACT_UUIDS = [
  '6ea143f7-8684-4f7a-befc-867e173ac1f9',
  '9336eb44-a44b-4a54-ba5c-4282f4e485d8',
  '430af7fe-2236-4db0-b2a3-235659e1e074'
]

export const DEFAULT_ALT_SKILL_LEVEL = 'Target'

const TRIAL_LICENSE_SUB_TYPE = 'trial'

const EnrolledGradeToGradeMapping = {
  'Pre-K': 'Pre-K',
  K: 'K',
  '1st': '1',
  '2nd': '2',
  '3rd': '3',
  '4th': '4',
  '5th': '5',
  '6th': '6',
  '7th': '7',
  '8th': '8',
  '9th': '9',
  '10th': '10',
  '11th': '11',
  '12th': '12'
}

const gradesSet = [
  'Pre-K',
  'K',
  '1st',
  '2nd',
  '3rd',
  '4th',
  '5th',
  '6th',
  '7th',
  '8th',
  '9th',
  '9-10th',
  '10th',
  '11th',
  '11-12th',
  '12th',
  'Mastery'
]

const enrolledGradesSet = [
  { name: 'Pre-Kindergarten', val: 'Pre-K' },
  { name: 'Kindergarten', val: 'K' },
  { name: '1st Grade', val: '1st' },
  { name: '2nd Grade', val: '2nd' },
  { name: '3rd Grade', val: '3rd' },
  { name: '4th Grade', val: '4th' },
  { name: '5th Grade', val: '5th' },
  { name: '6th Grade', val: '6th' },
  { name: '7th Grade', val: '7th' },
  { name: '8th Grade', val: '8th' },
  { name: '9th Grade', val: '9th' },
  { name: '10th Grade', val: '10th' },
  { name: '11th Grade', val: '11th' },
  { name: '12th Grade', val: '12th' }
]

export function getSkillTypes() {
  return skillTypes
}

export function getGradesSet() {
  return gradesSet.slice()
}

export function getEnrolledGradesSet() {
  return enrolledGradesSet.slice()
}

export function returnGradeFromEnrolledGrade(grade) {
  return EnrolledGradeToGradeMapping[grade]
}

export function getGradeLabelForGradeLevel(gradeLevel) {
  let gradeOption = enrolledGradesSet.filter(({ val }) => gradeLevel === val)[0]
  if (gradeOption && gradeOption.name) {
    return gradeOption.name
  }
  return gradeLevel
}

/**
 * @param {string} text
 * @param {{ studentName?: string, studentGrade?: string, dateStr?: string }} placeholders
 */
export function populatePlaceholderText(text, { studentName, studentGrade, dateStr }) {
  if (studentGrade) {
    text = text.replace(/\{enrolledGradeLevel\}/g, getGradeLabelForGradeLevel(studentGrade).toLowerCase())
  }
  if (studentName) {
    text = text.replace(/\{student\}/g, studentName)
    text = text.replace(/\{pronoun\}/g, studentName)
  }

  if (dateStr) {
    text = text.replace(/\{date\}/g, dateStr)
  }
  return text
}

async function fetchIgnoringNetworkErrors(url) {
  try {
    return await fetch(url, { method: 'GET', credentials: 'include' })
  } catch (error) {
    console.error('Problem reaching Present Levels', error)
    return undefined
  }
}

export async function getSkillBySubject(subject, licenseSubType, contentProfile) {
  const options = { license_sub_type: licenseSubType, content_profile: contentProfile }
  const queryString = `subject=${encodeURIComponent(subject)}&` + new URLSearchParams(options).toString()
  const response = await fetchIgnoringNetworkErrors(`${goalbookUrls.presentlevels}/presentlevels/api/v1/skills?${queryString}`)
  if (!response) {
    return
  }
  if (!response.ok) {
    console.error('Problem getting skills', response)
    return
  }
  try {
    return await response.json()
  } catch (error) {
    console.error('Problem getting skills json', error)
    return
  }
}

function lockStatementsByLicenseSubType(statements, licenseSubType) {
  if (licenseSubType !== TRIAL_LICENSE_SUB_TYPE || statements.length === 0) {
    return statements
  }
  const sortedStatements = statements.slice().sort((a, b) => parseInt(a.level_order) - parseInt(b.level_order))
  if (sortedStatements[0].skill_type === 'skill') {
    // only the 2 highest levels available for skills:
    for (let i = sortedStatements.length - 3; i >= 0; i--) {
      sortedStatements[i].locked = true
    }
    return sortedStatements
  }
  if (!UNLOCKED_IMPACT_UUIDS.includes(sortedStatements[0].skill_uuid)) {
    // aside from specific impacts, all impact statements should be locked
    for (let i = 0; i < statements.length; i++) {
      statements[i].locked = true
    }
  }
  return statements
}

/**
 * @param {string} skillUuid
 * @param {string} contentProfile
 * @param {string} licenseSubType
 * @param {string | null} [subject]
 */
export async function getStatementsBySkill(skillUuid, contentProfile, licenseSubType, subject = null) {
  const options = { skill: skillUuid, content_profile: contentProfile }
  const queryString = subject
    ? new URLSearchParams(options).toString() + `&subject=${encodeURIComponent(subject)}&`
    : new URLSearchParams(options).toString()
  const response = await fetchIgnoringNetworkErrors(`${goalbookUrls.presentlevels}/presentlevels/api/v1/statements?${queryString}`)
  if (!response) {
    return
  }
  if (response.ok) {
    try {
      const json = await response.json()
      if (json.statements) {
        json.statements = lockStatementsByLicenseSubType(json.statements, licenseSubType)
      }
      return json
    } catch (error) {
      console.error('Problem getting statement json', error)
      return
    }
  } else {
    console.error('Problem getting statements', response)
    return
  }
}

export async function getEffectStatementsBySubjectAndGrade(subject, grade) {
  const options = { grade }
  const queryString = `subject=${encodeURIComponent(subject)}&` + new URLSearchParams(options).toString()
  const response = await fetchIgnoringNetworkErrors(`${goalbookUrls.presentlevels}/presentlevels/api/v1/effects?${queryString}`)
  if (!response) {
    return
  }
  if (response.ok) {
    try {
      const json = await response.json()
      return json
    } catch (error) {
      console.error('Problem getting effect statement json', error)
      return
    }
  } else {
    console.error('Problem getting effect statements', response)
    return
  }
}

export async function getImpactBySubject(subject, licenseSubType, contentProfile) {
  const options = { license_sub_type: licenseSubType, content_profile: contentProfile }
  const queryString = `subject=${encodeURIComponent(subject)}&` + new URLSearchParams(options).toString()
  const response = await fetch(`${goalbookUrls.presentlevels}/presentlevels/api/v1/impacts?${queryString}`, {
    method: 'GET',
    credentials: 'include'
  })
  if (!response.ok) {
    throw new Error(`Problem getting impacts for ${subject}`)
  }
  return response.json()
}

const EFFECT_SKILL_ORDER = '20'

// Effect statements share one skill per subject and grade; it stands in for the impact skills.
export async function getSkillForEffectStatement(subject, grade) {
  const response = await getEffectStatementsBySubjectAndGrade(subject, grade)
  const [firstStatement] = response.statements
  if (!firstStatement) {
    return { subject, skills: [] }
  }
  return {
    subject,
    skills: [{
      locked: false,
      skill_name: firstStatement.skill_name,
      skill_uuid: firstStatement.skill_uuid,
      skill_order: EFFECT_SKILL_ORDER,
      subject
    }]
  }
}

export async function postPresentLevelStudentStatement({
  uuid,
  statementUuid,
  subject,
  skillLevel,
  skillUuid,
  statementText,
  defaultStatement,
  multiSelect = false
}) {
  const data = {
    subject_name: subject,
    skill_name: skillLevel,
    skill_uuid: skillUuid,
    statement_text: statementText,
    selected_statement: defaultStatement,
    is_selected: true
  }
  if (statementUuid) {
    data.uuid = statementUuid
  }
  const requestFn = () =>
    fetch(`${goalbookUrls.presentlevels}/presentlevels/api/v1/student/${uuid}?multiselect=${multiSelect}`, {
      method: 'POST',
      credentials: 'include',
      headers: getCSRFHeaders(),
      body: JSON.stringify(data)
    })
  return getHandleRequest()({ requestFn })
}

export async function putPresentLevelStudentStatementSelectStatus(uuid, statementUuid, status) {
  const requestFn = () =>
    fetch(`${goalbookUrls.presentlevels}/presentlevels/api/v1/student/${uuid}/deselect/${statementUuid}`, {
      method: 'PUT',
      credentials: 'include',
      headers: getCSRFHeaders(),
      body: JSON.stringify({ is_selected: status })
    })
  return getHandleRequest()({ requestFn })
}

export async function deleteAllPresentLevelStudentStatementsForSkill(plUuid, skillUuid) {
  const requestFn = () =>
    fetch(`${goalbookUrls.presentlevels}/presentlevels/api/v1/student/${plUuid}/skill/${skillUuid}`, {
      method: 'DELETE',
      credentials: 'include',
      headers: getCSRFHeaders()
    })
  return getHandleRequest()({ requestFn })
}

export async function assignPresentLevelsToFolder({ presentLevelsUuid, userUuid, folderUuid }) {
  const requestFn = () => fetch(`${goalbookUrls.presentlevels}/presentlevels/api/v1/student`, {
    method: 'POST',
    credentials: 'include',
    headers: getCSRFHeaders(),
    body: JSON.stringify({
      uuid: presentLevelsUuid,
      user_uuid: userUuid,
      student_uuid: folderUuid,
      student_pronoun: null,
      enrolled_grade_level: null,
      selected_subjects: null
    })
  })
  return getHandleRequest()({ requestFn })
}

export async function getPresentLevelStudentStatement(uuid, subject) {
  const requestFn = () =>
    fetch(`${goalbookUrls.presentlevels}/presentlevels/api/v1/student/${uuid}?subject=${encodeURIComponent(subject)}`, {
      method: 'GET',
      credentials: 'include',
      headers: getCSRFHeaders()
    })
  return getHandleRequest()({ requestFn })
}

export async function postPresentLevelStudent({ uuid, userUuid, name, pronoun, enrolledGrade, subjects }) {
  const requestFn = () =>
    fetch(`${goalbookUrls.presentlevels}/presentlevels/api/v1/student`, {
      method: 'POST',
      credentials: 'include',
      headers: getCSRFHeaders(),
      body: JSON.stringify({
        uuid,
        user_uuid: userUuid,
        student_name: name,
        student_pronoun: pronoun,
        enrolled_grade_level: enrolledGrade,
        selected_subjects: subjects
      })
    })
  return getHandleRequest()({ requestFn })
}

export async function getPresentLevelStudentStatementDraft(uuid, params = {}) {
  const requestFn = () =>
    fetch(`${goalbookUrls.presentlevels}/presentlevels/api/v1/student/${uuid}/draft?${new URLSearchParams(params)}`, {
      method: 'GET',
      credentials: 'include',
      headers: getCSRFHeaders()
    })
  return getHandleRequest()({ requestFn })
}
